import React, { useEffect, useState } from 'react';
import { Rocket } from 'lucide-react';
import Modal from '../common/Modal';
import Button from '../common/Button';
import FormControl from '../common/FormControl';
import Toast, { ToastMessage } from '../common/Toast';
import {
  programInstanceService,
  agencyService,
  type ProgramTemplate,
  type LaunchUser,
  type Agency,
  type ProgramLaunchRequest,
} from '../../services/auditService';
import {
  defaultStartDate,
  defaultDueDate,
  type Cadence,
} from '../../utils/cadenceDefaults';
import { useFieldValidation } from '../../validation/useFieldValidation';
import { ValidatedDatePicker } from '../../validation/ValidatedFields';
import { FieldSpec } from '../../validation/engine';
import { usePermission, NOT_AUTHORISED_MSG } from '../../contexts/PermissionContext';

/**
 * LaunchProgramModal — minimal launch form: Start/Due dates and an optional Agency.
 * The accountable user defaults to the agency head on the backend; no user selection here.
 */

export interface LaunchProgramModalProps {
  isOpen: boolean;
  onClose: () => void;
  template: ProgramTemplate | null;
  currentUser: LaunchUser;
  onLaunched: () => void;
}

const LaunchProgramModal: React.FC<LaunchProgramModalProps> = ({
  isOpen,
  onClose,
  template,
  currentUser: _currentUser,
  onLaunched,
}) => {
  const [startDate, setStartDate] = useState<string>('');
  const [dueDate, setDueDate]     = useState<string>('');
  const [dueDirty, setDueDirty]   = useState<boolean>(false);
  const [agencyId, setAgencyId]   = useState<number | null>(null);
  const [agencies, setAgencies]   = useState<Agency[]>([]);
  const [submitting, setSubmitting]     = useState<boolean>(false);
  const [toast, setToast]               = useState<ToastMessage | null>(null);
  const vh = useFieldValidation();
  const { allowed: canModify, loading: permLoading } = usePermission('ADT_PROGRAMS', 'view+modify');

  const cadence = (template?.cadence ?? 'Ad-hoc') as Cadence;

  // Seed dates + the template's agency (if any) on open.
  useEffect(() => {
    if (!isOpen || !template) return;
    const s = defaultStartDate(cadence);
    setStartDate(s);
    setDueDate(defaultDueDate(cadence, s));
    setDueDirty(false);
    setAgencyId(template.agency_id ?? null);
    vh.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, template?.id]);

  // Recompute due_date when start changes, unless the user edited it.
  useEffect(() => {
    if (!isOpen || !template || dueDirty || !startDate) return;
    setDueDate(defaultDueDate(cadence, startDate));
  }, [startDate, cadence, isOpen, template, dueDirty]);

  // Load the entity's agencies once on open (for the Agency picker).
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    agencyService.list()
      .then((a) => { if (!cancelled) setAgencies(a.filter((x) => (x.is_active ?? 1) === 1 && x.workflow_state === 'approved')); })
      .catch(() => { if (!cancelled) setAgencies([]); });
    return () => { cancelled = true; };
  }, [isOpen]);

  const datesValid    = !!(startDate && dueDate && startDate < dueDate);
  const canSubmit     = !!template && datesValid && !submitting;

  const handleSubmit = async () => {
    if (!template || !canSubmit) return;
    if (!canModify) { setToast({ type: 'error', message: NOT_AUTHORISED_MSG }); return; }
    const specs: FieldSpec[] = [
      { name: 'start_date', code: 305, value: startDate, required: true },
      { name: 'due_date', code: 305, value: dueDate, required: true },
    ];
    const formatOk = vh.validateAll(specs);
    // STRICT cross-field — mirrors the backend Pydantic model_validator (start_date >= due_date rejected)
    let rangeOk = true;
    if (startDate && dueDate && startDate >= dueDate) {
      vh.setError('due_date', 'Due date must be after the start date');
      rangeOk = false;
    }
    if (!formatOk || !rangeOk) return;
    setSubmitting(true);
    try {
      const payload: ProgramLaunchRequest = {
        program_template_id: template.id,
        start_date: startDate,
        due_date: dueDate,
        agency_id: agencyId,
        accountable_user_id: null,
        responsible_user_id: null,
        approver_user_id: null,
        audit_program_code: null,
      };
      await programInstanceService.launch(payload);
      setSubmitting(false);
      onLaunched();
      onClose();
    } catch (err: any) {
      const msg = err?.response?.data?.detail ?? err?.message ?? 'Launch failed.';
      setToast({ type: 'error', message: typeof msg === 'string' ? msg : 'Launch failed.' });
      setSubmitting(false);
    }
  };

  if (!isOpen || !template) return null;

  const agencyOptions = agencies.map((a) => ({ value: a.id, label: a.audit_agency_name }));

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="md" closeOnEscape closeOnOverlayClick>
        <Modal.Header>
          <span className="modal-header-icon-row">
            <Rocket size={16} />
            Launch Program: {template.audit_program_name}
          </span>
        </Modal.Header>

        <Modal.Body>
          <form id="launch-program-form" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} className="flex flex-col gap-y-4">
            <div className="grid grid-cols-2 gap-x-6 gap-y-4">
              <ValidatedDatePicker
                vh={vh}
                name="start_date"
                code={305}
                required
                label="Start Date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="INP_launch_program_start_date"
              />
              <ValidatedDatePicker
                vh={vh}
                name="due_date"
                code={305}
                required
                label="Due Date"
                value={dueDate}
                onChange={(e) => { setDueDate(e.target.value); setDueDirty(true); }}
                data-testid="INP_launch_program_due_date"
              />
            </div>

            <FormControl.SearchSelect
              label="Agency"
              options={agencyOptions}
              value={agencyId}
              clearable
              placeholder="— none —"
              onChange={(v) => { setAgencyId(v ? Number(v) : null); }}
              data-testid="SEL_launch_program_agency"
            />
          </form>
        </Modal.Body>

        <Modal.Footer>
          <Button
            type="submit"
            form="launch-program-form"
            variant="fill"
            icon={Rocket}
            disabled={permLoading || !canModify || !canSubmit}
            title={!canModify ? NOT_AUTHORISED_MSG : undefined}
            data-testid="BTN_launch_program_submit"
          >
            Launch
          </Button>
        </Modal.Footer>
      </Modal>

      <Toast toast={toast} onClose={() => setToast(null)} />
    </>
  );
};

export default LaunchProgramModal;
